import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Layout";
import { CloudArrowUp, MagicWand, ShieldCheck, Rocket, ArrowRight, CheckCircle } from "@phosphor-icons/react";

const STEPS = [
  { t: "Upload", d: "Drop your exported project. A ZIP is all we need." },
  { t: "We prepare it", d: "We make it independent — no Emergent lock-in." },
  { t: "Go live", d: "One button. Your own live web address." },
];

const FEATURES = [
  { Icon: MagicWand, t: "No terminals, ever", d: "No Docker, no servers, no config files. We handle all of it behind the scenes." },
  { Icon: ShieldCheck, t: "Your code stays yours", d: "Download a fully independent, self-hostable version any time. No lock-in." },
  { Icon: Rocket, t: "Live in one click", d: "We build, test, provision and deploy — then hand you a working web address." },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <div className="blob left-[-10%] top-[-5%] h-80 w-80 bg-accent" />
      <div className="blob right-[-8%] top-[20%] h-96 w-96 bg-success/40" />
      <div className="noise-overlay absolute inset-0" />

      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-6 py-6">
        <Logo />
        <div className="flex items-center gap-2">
          <Button variant="ghost" className="rounded-full" onClick={() => navigate("/login")} data-testid="nav-login-btn">Sign in</Button>
          <Button className="rounded-full active:scale-95" onClick={() => navigate("/register")} data-testid="nav-register-btn">Get started</Button>
        </div>
      </header>

      <section className="relative z-10 mx-auto max-w-4xl px-6 pt-16 text-center sm:pt-24">
        <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="mb-5 inline-block rounded-full border border-border bg-card px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          Hosting for people who don't do hosting
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="font-display text-5xl font-bold leading-[0.95] tracking-tight sm:text-7xl">
          Hosting an app should feel like uploading a picture.
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
          className="mx-auto mt-6 max-w-xl text-lg text-muted-foreground">
          Upload your project. We make it independent, test it, and put it online — all without a single command.
        </motion.p>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          onClick={() => navigate("/register")} data-testid="hero-dropzone"
          className="float-slow group relative mx-auto mt-12 max-w-xl cursor-pointer rounded-3xl border-2 border-dashed border-border bg-card p-12 shadow-sm transition-colors duration-200 hover:border-foreground/40">
          <div className="pulse-ring relative mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-secondary">
            <CloudArrowUp size={32} weight="duotone" />
          </div>
          <p className="mt-6 font-display text-xl font-medium">Drop your project here</p>
          <p className="mt-1 text-sm text-muted-foreground">ZIP export from Emergent, Bolt, Lovable, GitHub…</p>
          <Button className="mt-6 rounded-full active:scale-95">Start free <ArrowRight size={16} weight="bold" /></Button>
        </motion.div>
      </section>

      <section className="relative z-10 mx-auto mt-28 max-w-5xl px-6">
        <div className="grid gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <motion.div key={s.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-border bg-card p-8">
              <div className="mb-4 grid h-9 w-9 place-items-center rounded-full bg-primary text-sm font-bold text-primary-foreground">{i + 1}</div>
              <h3 className="font-display text-xl font-medium">{s.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="relative z-10 mx-auto mt-24 max-w-5xl px-6 pb-32">
        <div className="grid gap-6 md:grid-cols-3">
          {FEATURES.map(({ Icon, t, d }, i) => (
            <motion.div key={t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="rounded-2xl border border-border bg-card p-8 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
              <Icon size={30} weight="duotone" className="text-foreground" />
              <h3 className="mt-4 font-display text-xl font-medium">{t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{d}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-16 rounded-3xl border border-border bg-primary p-12 text-center text-primary-foreground">
          <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Ready to go live?</h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-primary-foreground/70">Create an account and host your first app in minutes.</p>
          <Button variant="secondary" className="mt-6 rounded-full active:scale-95" onClick={() => navigate("/register")} data-testid="cta-register-btn">
            Get started free <ArrowRight size={16} weight="bold" />
          </Button>
        </div>
      </section>
    </div>
  );
}
